import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { ACCESS_DENIED_URL } from './accessDenied';

import type { SessionUser } from '@/types/session/SessionUser';
import type { ReactNode } from 'react';

import { userInstanceAtom } from '@/atoms/userDetails';
import { LockedFeatureState } from '@/components/ProGate/LockedFeatureState';
import { PerformanceProFeature } from '@/constants/PerformanceProFeature';
import { useProGate } from '@/hooks/performance-tier/useProGate';
import { ProGateOutcome } from '@/utils/performanceTier';

/**
 * Guards the `./performance-profile` entry point by the company's tier (#17451).
 *
 * The host mounts this entry point straight from its colleague profile, so the module's own
 * `ProFeatureRoute` never runs: opened by its address, a company without Performance PRO read the
 * whole profile behind the locked tab. A reader who could buy it is offered the upgrade; anyone else
 * is refused, as a missing permission is. The profile is not mounted, so none of its data is read.
 *
 * `user` is the reader the host sent. `ModuleProviders` stores it in an effect, so until then the tier
 * reads as PRO (no access keys yet): the gate waits for that reader rather than mount the profile.
 */
export const PerformanceProfileProGate = ({
    user,
    children,
}: {
    user?: SessionUser;
    children: ReactNode;
}) => {
    const reader = useAtomValue(userInstanceAtom);
    const outcome = useProGate();
    const isAwaitingReader = Boolean(user) && reader.userId !== user?.userId;

    useEffect(() => {
        if (!isAwaitingReader && outcome === ProGateOutcome.Hide) {
            window.location.href = ACCESS_DENIED_URL;
        }
    }, [isAwaitingReader, outcome]);

    if (isAwaitingReader) {
        return null;
    }

    if (outcome === ProGateOutcome.Lock) {
        return <LockedFeatureState feature={PerformanceProFeature.PerformanceProfile} />;
    }

    if (outcome !== ProGateOutcome.Show) {
        return null;
    }

    return <>{children}</>;
};
